import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Campaign,
  CampaignItem,
  Cliente,
  CsvTransientLeadInput,
  LostLeadCard,
  LostLeadReason,
} from '../../types';
import { apiService } from '../../services/api';
import './CampaignSourceManager.css';

type SourceTab = 'perdidos' | 'inativos' | 'parceiros' | 'csv';
type InterestFilter = 'all' | 'interested' | 'not_interested';
type SortMode = 'followupAsc' | 'followupDesc';
type LostPeriod = 'all' | '1' | '3' | '6' | '12';
type DistanceFilter = 'all' | '30' | '60' | '90' | '180' | '365';
/** Ordenação da lista de inativos por `dataPerda` (backend `sortOrder` asc/desc). */
type InativosDataPerdaSort = 'asc' | 'desc';

export type CampaignSourceSelectionTotals = {
  total: number;
  perdidos: number;
  inativos: number;
  parceiros: number;
  csv: number;
};

export type CampaignSourceManagerSubmitResult = {
  ok: boolean;
  errorCode?: 'empty' | 'api';
  errorMessage?: string;
};

export type CampaignSourceManagerHandle = {
  submitSelectionsToCampaign: () => Promise<CampaignSourceManagerSubmitResult>;
};

interface CampaignSourceManagerProps {
  /** Só no modo standalone: botão «Voltar ao kanban». */
  onBackToKanban?: () => void;
  /** Modo assistente: fixa a campanha e oculta o seletor. */
  lockedCampaignId?: string;
  /** Oculta título standalone / botão voltar (wizard). */
  embedded?: boolean;
  /** Passo 2 do wizard: tabs dentro do card da lista, sem painel lateral nem botão «Adicionar». */
  variant?: 'default' | 'wizard';
  /** Chamado quando as contagens de seleção mudam (só em variant wizard). */
  onWizardSelectionTotals?: (totals: CampaignSourceSelectionTotals) => void;
}

function formatDate(value?: string | null): string {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('pt-BR');
}

function isInterested(card: LostLeadCard): boolean {
  return card.interessado || (card.interessadoLigarEmMeses || 0) > 0;
}

function isRh(c: Cliente): boolean {
  return c.dadosAdicionais?.tipoContato === 'rh';
}

function withinDays(dateStr: string | undefined, maxDays: number): boolean {
  if (!dateStr) return true;
  const diff = Date.now() - new Date(dateStr).getTime();
  return diff >= maxDays * 86400000;
}

function ForwardIcon() {
  return (
    <svg className="csm-forward-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 12H9" strokeLinecap="round" />
      <path d="M9 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const MAX_CSV_TRANSIENT_LEADS = 2000;

/** Ordem estável para query `lostReasons` e checkboxes Inativos (F6.6). */
const INATIVOS_LOST_REASON_ORDER: LostLeadReason[] = ['idle_timeout', 'low_intent', 'non_intention'];

const INATIVOS_LOST_REASON_LABELS: Record<LostLeadReason, string> = {
  idle_timeout: 'Timeout (sem resposta)',
  low_intent: 'Baixa intenção',
  non_intention: 'Sem intenção',
};

function formatInativoMotivoLabel(reason?: LostLeadReason): string {
  if (!reason) return '—';
  return INATIVOS_LOST_REASON_LABELS[reason] ?? reason;
}

function normalizePhone(value: string): string {
  return (value || '').replace(/@lid$/i, '').replace(/\D/g, '');
}

async function parseCsvInChunks(text: string): Promise<{
  parsed: CsvTransientLeadInput[];
  invalidRows: number;
  duplicateRows: number;
}> {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return { parsed: [], invalidRows: 0, duplicateRows: 0 };

  const firstLower = lines[0].toLowerCase();
  const hasHeader = firstLower.includes('nome') || firstLower.includes('telefone');
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const parsed: CsvTransientLeadInput[] = [];
  const seen = new Set<string>();
  let invalidRows = 0;
  let duplicateRows = 0;
  const CHUNK = 200;

  for (let i = 0; i < dataLines.length; i += CHUNK) {
    const chunk = dataLines.slice(i, i + CHUNK);
    for (const line of chunk) {
      const parts = line.split(/[,;\t]/).map((p) => p.trim());
      if (parts.length < 2) {
        invalidRows += 1;
        continue;
      }
      const [nome, telefone, third, fourth] = parts;
      const phoneNormalized = normalizePhone(telefone || '');
      if (!nome || !telefone || !phoneNormalized) {
        invalidRows += 1;
        continue;
      }
      if (seen.has(phoneNormalized)) {
        duplicateRows += 1;
        continue;
      }
      seen.add(phoneNormalized);

      const base: CsvTransientLeadInput = { nome, telefone, phoneNormalized };
      if (parts.length >= 4) {
        if (third) base.cpf = third;
        if (fourth) base.email = fourth;
      } else if (parts.length === 3) {
        if (third?.includes('@')) base.email = third;
        else if (third) base.cpf = third;
      }
      parsed.push(base);
      if (parsed.length >= MAX_CSV_TRANSIENT_LEADS) {
        return { parsed, invalidRows, duplicateRows };
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  return { parsed, invalidRows, duplicateRows };
}

export const CampaignSourceManager = forwardRef<CampaignSourceManagerHandle, CampaignSourceManagerProps>(
  function CampaignSourceManager(
    {
      onBackToKanban,
      lockedCampaignId,
      embedded,
      variant = 'default',
      onWizardSelectionTotals,
    },
    ref
  ) {
  const [activeTab, setActiveTab] = useState<SourceTab>('perdidos');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');

  // Shared campaign state
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [allCampaignItems, setAllCampaignItems] = useState<CampaignItem[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');

  // Cross-tab selections (persist across tab switches)
  const [selectedLostCardIds, setSelectedLostCardIds] = useState<string[]>([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<string[]>([]);
  const [selectedCsvLeadPhones, setSelectedCsvLeadPhones] = useState<string[]>([]);

  // Perdidos
  const [lostCards, setLostCards] = useState<LostLeadCard[]>([]);
  const [inativosCards, setInativosCards] = useState<LostLeadCard[]>([]);
  const [selectedInativosLostCardIds, setSelectedInativosLostCardIds] = useState<string[]>([]);
  const [periodFilter, setPeriodFilter] = useState<LostPeriod>('all');
  const [interestFilter, setInterestFilter] = useState<InterestFilter>('all');
  const [onlyInterestedThisMonth, setOnlyInterestedThisMonth] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('followupAsc');
  const [inativosDataPerdaSort, setInativosDataPerdaSort] = useState<InativosDataPerdaSort>('asc');
  /** Vazio = todos os motivos; um ou mais = OR no backend (`lostReasons`). */
  const [inativosLostReasonFilter, setInativosLostReasonFilter] = useState<LostLeadReason[]>([]);

  // Parceiros
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [partnerDistanceFilter, setPartnerDistanceFilter] = useState<DistanceFilter>('all');

  // CSV
  const [csvLeads, setCsvLeads] = useState<CsvTransientLeadInput[]>([]);
  const [csvText, setCsvText] = useState('');
  const [csvDistanceFilter, setCsvDistanceFilter] = useState<DistanceFilter>('all');
  const [importing, setImporting] = useState(false);

  const loadLostCards = useCallback(async () => {
    try {
      setLoading(true);
      const cards = await apiService.getLostLeadCards({
        stage: 'perdido',
        lostPeriodMonths: periodFilter === 'all' ? undefined : Number(periodFilter),
        interestFilter,
        onlyInterestedThisMonth,
        sortBy: 'interessadoLigarEmData',
        sortOrder: sortMode === 'followupAsc' ? 'asc' : 'desc',
      });
      setLostCards(cards);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar perdidos');
    } finally {
      setLoading(false);
    }
  }, [interestFilter, onlyInterestedThisMonth, periodFilter, sortMode]);

  const loadInativosCards = useCallback(async () => {
    try {
      setLoading(true);
      const lostReasonsFilter =
        inativosLostReasonFilter.length > 0 &&
        inativosLostReasonFilter.length < INATIVOS_LOST_REASON_ORDER.length
          ? [...inativosLostReasonFilter].sort(
              (a, b) =>
                INATIVOS_LOST_REASON_ORDER.indexOf(a) - INATIVOS_LOST_REASON_ORDER.indexOf(b)
            )
          : undefined;
      const cards = await apiService.getLostLeadCards({
        stage: 'perdido',
        lostCategory: 'inativo',
        sortBy: 'dataPerda',
        sortOrder: inativosDataPerdaSort,
        ...(lostReasonsFilter?.length ? { lostReasons: lostReasonsFilter } : {}),
      });
      setInativosCards(cards);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar inativos');
    } finally {
      setLoading(false);
    }
  }, [inativosDataPerdaSort, inativosLostReasonFilter]);

  const loadPartners = useCallback(async () => {
    try {
      const all = await apiService.getClientes();
      setClientes(all.filter(isRh));
    } catch (err: any) {
      console.error('Failed to load partners:', err);
    }
  }, []);

  const isWizard = variant === 'wizard';

  useEffect(() => {
    apiService.listCampaigns().then(setCampaigns).catch(console.error);
    apiService.listCampaignItems().then(setAllCampaignItems).catch(console.error);
    loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    if (isWizard) {
      void loadLostCards();
      void loadInativosCards();
      return;
    }
    if (activeTab === 'perdidos') void loadLostCards();
    if (activeTab === 'inativos') void loadInativosCards();
  }, [
    isWizard,
    activeTab,
    loadLostCards,
    loadInativosCards,
    interestFilter,
    onlyInterestedThisMonth,
    periodFilter,
    sortMode,
    inativosDataPerdaSort,
    inativosLostReasonFilter,
  ]);

  useEffect(() => {
    if (lockedCampaignId) {
      setSelectedCampaignId(lockedCampaignId);
    }
  }, [lockedCampaignId]);

  const manageCampaigns = useMemo(
    () => campaigns.filter((c) => c.viewerCanManage === true),
    [campaigns]
  );

  const activeCampaigns = useMemo(() => {
    const idsWithItems = new Set(allCampaignItems.map((i) => i.campaignId));
    return manageCampaigns.filter((c) => c.status !== 'encerrada' && !idsWithItems.has(c.campaignId));
  }, [manageCampaigns, allCampaignItems]);

  const activeCampaignIds = useMemo(
    () => new Set(campaigns.filter((c) => c.status === 'ativa').map((c) => c.campaignId)),
    [campaigns]
  );

  const activePartnerSourceIds = useMemo(() => {
    const ids = new Set<string>();
    allCampaignItems.forEach((item) => {
      if (
        item.sourceType === 'partner' &&
        item.sourceId &&
        activeCampaignIds.has(item.campaignId)
      ) {
        ids.add(item.sourceId);
      }
    });
    return ids;
  }, [allCampaignItems, activeCampaignIds]);

  // Filtered partners
  const filteredPartners = useMemo(() => {
    let list = clientes;
    if (partnerDistanceFilter !== 'all') {
      const days = Number(partnerDistanceFilter);
      list = list.filter((c) => withinDays(c.dadosAdicionais?.ultimoCampanhaEm, days));
    }
    return list;
  }, [clientes, partnerDistanceFilter]);

  // Filtered imported leads
  const filteredImported = useMemo(() => {
    void csvDistanceFilter;
    return csvLeads;
  }, [csvLeads, csvDistanceFilter]);

  const totalSelected =
    selectedLostCardIds.length +
    selectedInativosLostCardIds.length +
    selectedPartnerIds.length +
    selectedCsvLeadPhones.length;

  useEffect(() => {
    if (variant !== 'wizard' || !onWizardSelectionTotals) return;
    onWizardSelectionTotals({
      total: totalSelected,
      perdidos: selectedLostCardIds.length,
      inativos: selectedInativosLostCardIds.length,
      parceiros: selectedPartnerIds.length,
      csv: selectedCsvLeadPhones.length,
    });
  }, [
    variant,
    onWizardSelectionTotals,
    totalSelected,
    selectedLostCardIds.length,
    selectedInativosLostCardIds.length,
    selectedPartnerIds.length,
    selectedCsvLeadPhones.length,
  ]);

  useEffect(() => {
    const hasUnsavedCsvSession = csvLeads.length > 0 || !!csvText.trim();
    if (!hasUnsavedCsvSession) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [csvLeads.length, csvText]);

  const toggleLostCard = (id: string) => {
    setFeedback('');
    setSelectedLostCardIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleInativosLostCard = (id: string) => {
    setFeedback('');
    setSelectedInativosLostCardIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const togglePartner = (id: string) => {
    setFeedback('');
    setSelectedPartnerIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleCsvLead = (phoneNormalized: string) => {
    setFeedback('');
    setSelectedCsvLeadPhones((prev) =>
      prev.includes(phoneNormalized) ? prev.filter((x) => x !== phoneNormalized) : [...prev, phoneNormalized]
    );
  };

  const selectAllFilteredLost = () => {
    setFeedback('');
    const ids = lostCards.map((c) => c.lostCardId);
    setSelectedLostCardIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const selectAllFilteredInativos = () => {
    setFeedback('');
    const ids = inativosCards.map((c) => c.lostCardId);
    setSelectedInativosLostCardIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const selectAllFilteredPartners = () => {
    setFeedback('');
    const ids = filteredPartners
      .filter((c) => !activePartnerSourceIds.has(c.clienteId))
      .map((c) => c.clienteId);
    setSelectedPartnerIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const selectAllFilteredImported = () => {
    setFeedback('');
    const ids = filteredImported.map((il) => il.phoneNormalized);
    setSelectedCsvLeadPhones((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const handleConvertCsv = async () => {
    if (!csvText.trim()) return;
    try {
      setImporting(true);
      setError(null);
      const { parsed, invalidRows, duplicateRows } = await parseCsvInChunks(csvText);
      if (parsed.length === 0) {
        setError('Nenhum lead válido encontrado no CSV. Formato: nome,telefone ou nome,telefone,email');
        return;
      }
      const remainingCapacity = MAX_CSV_TRANSIENT_LEADS - csvLeads.length;
      if (remainingCapacity <= 0) {
        setError(`Limite de ${MAX_CSV_TRANSIENT_LEADS} leads atingido nesta sessão.`);
        return;
      }
      const accepted = parsed.slice(0, remainingCapacity);
      const incoming = new Set(csvLeads.map((l) => l.phoneNormalized));
      const unique = accepted.filter((lead) => !incoming.has(lead.phoneNormalized));
      const skippedSessionDuplicates = accepted.length - unique.length;
      setCsvLeads((prev) => [...prev, ...unique]);
      setFeedback(
        `${unique.length} lead(s) adicionados ao staging da sessão.` +
          (duplicateRows + skippedSessionDuplicates > 0
            ? ` ${duplicateRows + skippedSessionDuplicates} duplicado(s) ignorado(s).`
            : '') +
          (invalidRows > 0 ? ` ${invalidRows} linha(s) inválida(s).` : '') +
          (parsed.length > accepted.length ? ` Limite de ${MAX_CSV_TRANSIENT_LEADS} aplicado.` : '')
      );
      setCsvText('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao processar CSV');
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteCsvLead = (phoneNormalized: string) => {
    setCsvLeads((prev) => prev.filter((il) => il.phoneNormalized !== phoneNormalized));
    setSelectedCsvLeadPhones((prev) => prev.filter((x) => x !== phoneNormalized));
  };

  const addSelectionsToCampaign = useCallback(async (): Promise<CampaignSourceManagerSubmitResult> => {
    if (totalSelected === 0 || !selectedCampaignId) {
      return { ok: false, errorCode: 'empty' };
    }
    if (saving) {
      return { ok: false, errorCode: 'api', errorMessage: 'Aguarde…' };
    }

    try {
      setSaving(true);
      setError(null);
      const promises: Promise<any>[] = [];

      if (selectedLostCardIds.length > 0) {
        promises.push(apiService.addLostCardsToCampaign(selectedCampaignId, selectedLostCardIds));
      }
      if (selectedInativosLostCardIds.length > 0) {
        promises.push(apiService.addLostCardsToCampaign(selectedCampaignId, selectedInativosLostCardIds));
      }
      if (selectedPartnerIds.length > 0) {
        promises.push(apiService.addPartnersToCampaign(selectedCampaignId, selectedPartnerIds));
      }
      if (selectedCsvLeadPhones.length > 0) {
        const selectedLeads = csvLeads.filter((lead) => selectedCsvLeadPhones.includes(lead.phoneNormalized));
        promises.push(apiService.addCsvLeadsToCampaign(selectedCampaignId, selectedLeads));
      }

      const results = await Promise.all(promises);
      let added = 0;
      let skippedUnsub = 0;
      let duplicate = 0;
      let invalid = 0;
      for (const r of results) {
        if (r && typeof r === 'object' && 'items' in r) {
          added += r.items.length;
          skippedUnsub += (r.skippedUnsubscribed || []).length;
          duplicate += Number((r as any).duplicate || 0);
          invalid += Number((r as any).invalid || 0);
        }
      }
      const parts: string[] = [];
      if (added > 0) parts.push(`${added} item(ns) adicionado(s) à campanha.`);
      if (skippedUnsub > 0) {
        parts.push(
          `${skippedUnsub} contato(s) não incluído(s) — número descadastrado (opt-out).`
        );
      }
      if (duplicate > 0) parts.push(`${duplicate} duplicado(s) ignorado(s) na mesma campanha.`);
      if (invalid > 0) parts.push(`${invalid} linha(s) inválida(s).`);
      if (parts.length === 0) parts.push('Nenhuma alteração.');
      setFeedback(parts.join(' '));
      setSelectedLostCardIds([]);
      setSelectedInativosLostCardIds([]);
      setSelectedPartnerIds([]);
      setSelectedCsvLeadPhones([]);
      setSelectedCampaignId(lockedCampaignId || '');
      if (selectedCsvLeadPhones.length > 0) {
        const consumed = new Set(selectedCsvLeadPhones);
        setCsvLeads((prev) => prev.filter((lead) => !consumed.has(lead.phoneNormalized)));
      }

      await Promise.all([
        loadLostCards(),
        loadInativosCards(),
        loadPartners(),
        apiService.listCampaigns().then(setCampaigns),
        apiService.listCampaignItems().then(setAllCampaignItems),
      ]);
      return { ok: true };
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Erro ao adicionar itens à campanha';
      setError(message);
      return { ok: false, errorCode: 'api', errorMessage: message };
    } finally {
      setSaving(false);
    }
  }, [
    totalSelected,
    selectedCampaignId,
    saving,
    selectedLostCardIds,
    selectedInativosLostCardIds,
    selectedPartnerIds,
    selectedCsvLeadPhones,
    csvLeads,
    lockedCampaignId,
    loadLostCards,
    loadInativosCards,
    loadPartners,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      submitSelectionsToCampaign: () => addSelectionsToCampaign(),
    }),
    [addSelectionsToCampaign]
  );

  const handleAddToCampaign = () => void addSelectionsToCampaign();

  const distanceOptions = (
    <>
      <option value="all">Todos</option>
      <option value="30">Sem contato há 30d+</option>
      <option value="60">Sem contato há 60d+</option>
      <option value="90">Sem contato há 90d+</option>
      <option value="180">Sem contato há 6m+</option>
      <option value="365">Sem contato há 1a+</option>
    </>
  );

  return (
    <section
      className={`csm-module${embedded ? ' csm-module--embedded' : ''}${isWizard ? ' csm-module--wizard' : ''}`}
    >
      {!embedded && (
        <header className="csm-header">
          <div>
            <h2>Gestão de Campanhas</h2>
            <p>Selecione itens de diferentes fontes e adicione a uma campanha.</p>
          </div>
          {onBackToKanban ? (
            <button type="button" className="csm-back-button" onClick={onBackToKanban}>
              Voltar ao kanban
            </button>
          ) : null}
        </header>
      )}

      {!isWizard && (
        <div className="csm-tabs" role="tablist" aria-label="Origem dos contactos">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'perdidos'}
            className={`csm-tab ${activeTab === 'perdidos' ? 'active' : ''}`}
            onClick={() => setActiveTab('perdidos')}
          >
            Aguardando contato{selectedLostCardIds.length > 0 ? ` (${selectedLostCardIds.length})` : ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'inativos'}
            className={`csm-tab ${activeTab === 'inativos' ? 'active' : ''}`}
            onClick={() => setActiveTab('inativos')}
          >
            Inativos{selectedInativosLostCardIds.length > 0 ? ` (${selectedInativosLostCardIds.length})` : ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'parceiros'}
            className={`csm-tab ${activeTab === 'parceiros' ? 'active' : ''}`}
            onClick={() => setActiveTab('parceiros')}
          >
            Parceiros{selectedPartnerIds.length > 0 ? ` (${selectedPartnerIds.length})` : ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'csv'}
            className={`csm-tab ${activeTab === 'csv' ? 'active' : ''}`}
            onClick={() => setActiveTab('csv')}
          >
            CSV{selectedCsvLeadPhones.length > 0 ? ` (${selectedCsvLeadPhones.length})` : ''}
          </button>
        </div>
      )}

      {error && <div className="csm-error">{error}</div>}
      {feedback && <div className="csm-feedback">{feedback}</div>}

      <div className={isWizard ? 'csm-wizard-main' : 'csm-grid'}>
        <section className={`csm-results-panel${isWizard ? ' csm-results-panel--wizard' : ''}`}>
          {isWizard && (
            <div className="csm-tabs csm-tabs--in-card" role="tablist" aria-label="Fontes de contactos">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'perdidos'}
                className={`csm-tab csm-tab--wizard ${activeTab === 'perdidos' ? 'active' : ''}`}
                onClick={() => setActiveTab('perdidos')}
              >
                Perdidos ({loading ? '…' : lostCards.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'inativos'}
                className={`csm-tab csm-tab--wizard ${activeTab === 'inativos' ? 'active' : ''}`}
                onClick={() => setActiveTab('inativos')}
              >
                Inativos ({loading ? '…' : inativosCards.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'parceiros'}
                className={`csm-tab csm-tab--wizard ${activeTab === 'parceiros' ? 'active' : ''}`}
                onClick={() => setActiveTab('parceiros')}
              >
                Parceiros ({filteredPartners.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'csv'}
                className={`csm-tab csm-tab--wizard ${activeTab === 'csv' ? 'active' : ''}`}
                onClick={() => setActiveTab('csv')}
              >
                CSV ({filteredImported.length})
              </button>
            </div>
          )}
          {/* PERDIDOS TAB */}
          {activeTab === 'perdidos' && (
            <>
              <div className="csm-filters-row">
                <label>
                  Perda registrada há
                  <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value as LostPeriod)}>
                    <option value="all">Todos os períodos</option>
                    <option value="1">1 mês</option>
                    <option value="3">3 meses</option>
                    <option value="6">6 meses</option>
                    <option value="12">12 meses</option>
                  </select>
                </label>
                <label>
                  Interesse
                  <select value={interestFilter} onChange={(e) => setInterestFilter(e.target.value as InterestFilter)}>
                    <option value="all">Todos</option>
                    <option value="interested">Interessados</option>
                    <option value="not_interested">Não interessados</option>
                  </select>
                </label>
                <label>
                  Ordenar por
                  <select value={sortMode} onChange={(e) => setSortMode(e.target.value as SortMode)}>
                    <option value="followupAsc">Data calculada (mais próxima)</option>
                    <option value="followupDesc">Data calculada (mais distante)</option>
                  </select>
                </label>
                <label className="csm-toggle">
                  <input
                    type="checkbox"
                    checked={onlyInterestedThisMonth}
                    onChange={(e) => setOnlyInterestedThisMonth(e.target.checked)}
                  />
                  Ligar este mês
                </label>
              </div>

              <header>
                <h3>Aguardando contato (filtrados)</h3>
                <div className="csm-list-header-actions">
                  <button
                    type="button"
                    className="csm-send-all-btn"
                    onClick={selectAllFilteredLost}
                    disabled={loading || lostCards.length === 0}
                    title="Selecionar todos os cards filtrados"
                  >
                    <ForwardIcon />
                    Enviar todos
                  </button>
                  <span className="csm-list-count">{loading ? '...' : `${lostCards.length} itens`}</span>
                </div>
              </header>

              <div className="csm-list">
                {loading ? (
                  <p className="csm-empty">Carregando cards...</p>
                ) : lostCards.length === 0 ? (
                  <p className="csm-empty">Nenhum card encontrado.</p>
                ) : (
                  lostCards.map((card) => (
                    <article key={card.lostCardId} className="csm-item-card">
                      <label className="csm-item-select">
                        <input
                          type="checkbox"
                          checked={selectedLostCardIds.includes(card.lostCardId)}
                          onChange={() => toggleLostCard(card.lostCardId)}
                        />
                        Selecionar
                      </label>
                      <strong>{card.nome}</strong>
                      <p>{card.observacao || 'Sem observação'}</p>
                      <div className="csm-item-meta">
                        <span className="csm-stage-pill">
                          {card.pipelineStage === 'em_campanha'
                            ? 'Em campanha'
                            : 'Aguardando contato'}
                        </span>
                        <span>{isInterested(card) ? 'Interessado' : 'Não interessado'}</span>
                        <span>Ligar em {card.interessadoLigarEmMeses || 0}m</span>
                        <span>Calculada: {formatDate(card.interessadoLigarEmData)}</span>
                        <span>Perda: {formatDate(card.dataPerda)}</span>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </>
          )}

          {activeTab === 'inativos' && (
            <>
              <div className="csm-filters-row">
                <div
                  className="csm-motivo-filter"
                  role="group"
                  aria-labelledby="csm-inativos-motivo-label"
                >
                  <div id="csm-inativos-motivo-label" className="csm-motivo-filter-heading">
                    <span>Motivo</span>
                    <span className="csm-motivo-filter-sub">Fonte do arquivo</span>
                  </div>
                  <div className="csm-motivo-checks">
                    {INATIVOS_LOST_REASON_ORDER.map((reason) => (
                      <label key={reason} className="csm-motivo-check">
                        <input
                          type="checkbox"
                          checked={inativosLostReasonFilter.includes(reason)}
                          onChange={() =>
                            setInativosLostReasonFilter((prev) =>
                              prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
                            )
                          }
                        />
                        {INATIVOS_LOST_REASON_LABELS[reason]}
                      </label>
                    ))}
                  </div>
                </div>
                <label>
                  Ordenar por
                  <select
                    value={inativosDataPerdaSort}
                    onChange={(e) => setInativosDataPerdaSort(e.target.value as InativosDataPerdaSort)}
                  >
                    <option value="asc">Data de perda (mais distante no tempo)</option>
                    <option value="desc">Data de perda (mais próxima)</option>
                  </select>
                </label>
              </div>
              <header>
                <h3>Inativos (fechamento sem resposta)</h3>
                <div className="csm-list-header-actions">
                  <button
                    type="button"
                    className="csm-send-all-btn"
                    onClick={selectAllFilteredInativos}
                    disabled={loading || inativosCards.length === 0}
                    title="Selecionar todos os inativos listados"
                  >
                    <ForwardIcon />
                    Enviar todos
                  </button>
                  <span className="csm-list-count">{loading ? '...' : `${inativosCards.length} itens`}</span>
                </div>
              </header>

              <div className="csm-list">
                {loading ? (
                  <p className="csm-empty">Carregando cards...</p>
                ) : inativosCards.length === 0 ? (
                  <p className="csm-empty">Nenhum inativo encontrado.</p>
                ) : (
                  inativosCards.map((card) => (
                    <article key={card.lostCardId} className="csm-item-card">
                      <label className="csm-item-select">
                        <input
                          type="checkbox"
                          checked={selectedInativosLostCardIds.includes(card.lostCardId)}
                          onChange={() => toggleInativosLostCard(card.lostCardId)}
                        />
                        Selecionar
                      </label>
                      <strong>{card.nome}</strong>
                      <p>{card.observacao || 'Sem observação'}</p>
                      <div className="csm-item-meta">
                        <span className="csm-stage-pill">Inativo</span>
                        <span>Motivo: {formatInativoMotivoLabel(card.lostReason)}</span>
                        <span>Seguimento: {formatDate(card.interessadoLigarEmData)}</span>
                        <span>Perda: {formatDate(card.dataPerda)}</span>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </>
          )}

          {/* PARCEIROS TAB */}
          {activeTab === 'parceiros' && (
            <>
              <div className="csm-filters-row">
                <label>
                  Último contato
                  <select value={partnerDistanceFilter} onChange={(e) => setPartnerDistanceFilter(e.target.value as DistanceFilter)}>
                    {distanceOptions}
                  </select>
                </label>
              </div>

              <header>
                <h3>Parceiros (Contatos RH)</h3>
                <div className="csm-list-header-actions">
                  <button
                    type="button"
                    className="csm-send-all-btn"
                    onClick={selectAllFilteredPartners}
                    disabled={
                      filteredPartners.filter((c) => !activePartnerSourceIds.has(c.clienteId)).length === 0
                    }
                    title="Selecionar todos os parceiros disponíveis (fora de campanha)"
                  >
                    <ForwardIcon />
                    Enviar todos
                  </button>
                  <span className="csm-list-count">{filteredPartners.length} itens</span>
                </div>
              </header>

              <div className="csm-list">
                {filteredPartners.length === 0 ? (
                  <p className="csm-empty">Nenhum parceiro encontrado.</p>
                ) : (
                  filteredPartners.map((c) => {
                    const inCampaign = activePartnerSourceIds.has(c.clienteId);
                    return (
                      <article key={c.clienteId} className={`csm-item-card${inCampaign ? ' disabled' : ''}`}>
                        <label className="csm-item-select">
                          <input
                            type="checkbox"
                            checked={selectedPartnerIds.includes(c.clienteId)}
                            onChange={() => togglePartner(c.clienteId)}
                            disabled={inCampaign}
                          />
                          Selecionar
                        </label>
                        <strong>{c.dadosAdicionais?.nomeContato || c.nome}</strong>
                        <div className="csm-item-meta">
                          <span>{c.telefone}</span>
                          <span>{c.dadosAdicionais?.nomeEmpresa || '-'}</span>
                          <span>Último: {formatDate(c.dadosAdicionais?.ultimoCampanhaEm) || 'Nunca'}</span>
                          {inCampaign && <span className="csm-badge-campaign">Em campanha</span>}
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* CSV TAB */}
          {activeTab === 'csv' && (
            <>
              <div className="csm-csv-area">
                <textarea
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  placeholder="Cole CSV aqui: nome,telefone ou nome,telefone,email (uma linha por lead)"
                  disabled={importing}
                />
                <p className="text-muted" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                  Dados do CSV ficam apenas nesta sessão do navegador e são perdidos ao sair/recarregar. Limite de{' '}
                  {MAX_CSV_TRANSIENT_LEADS.toLocaleString('pt-BR')} leads por importação.
                </p>
                <div className="csm-csv-actions">
                  <button
                    className="csm-secondary-button"
                    onClick={handleConvertCsv}
                    disabled={!csvText.trim() || importing}
                  >
                    {importing ? 'Importando...' : 'Converter CSV'}
                  </button>
                </div>
              </div>

              <div className="csm-filters-row">
                <label>
                  Último contato
                  <select value={csvDistanceFilter} onChange={(e) => setCsvDistanceFilter(e.target.value as DistanceFilter)}>
                    {distanceOptions}
                  </select>
                </label>
              </div>

              <header>
                <h3>Leads CSV em memória</h3>
                <div className="csm-list-header-actions">
                  <button
                    type="button"
                    className="csm-send-all-btn"
                    onClick={selectAllFilteredImported}
                    disabled={filteredImported.length === 0}
                    title="Selecionar todos os contatos CSV visíveis"
                  >
                    <ForwardIcon />
                    Enviar todos
                  </button>
                  <span className="csm-list-count">{filteredImported.length} itens</span>
                </div>
              </header>

              <div className="csm-list">
                {filteredImported.length === 0 ? (
                  <p className="csm-empty">Nenhum lead CSV disponível nesta sessão.</p>
                ) : (
                  filteredImported.map((il) => {
                    return (
                      <article key={il.phoneNormalized} className="csm-item-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <label className="csm-item-select">
                            <input
                              type="checkbox"
                              checked={selectedCsvLeadPhones.includes(il.phoneNormalized)}
                              onChange={() => toggleCsvLead(il.phoneNormalized)}
                            />
                            Selecionar
                          </label>
                          <button className="csm-delete-btn" onClick={() => handleDeleteCsvLead(il.phoneNormalized)}>
                            Excluir
                          </button>
                        </div>
                        <strong>{il.nome}</strong>
                        <div className="csm-item-meta">
                          <span>{il.telefone}</span>
                          {il.email && <span>{il.email}</span>}
                          <span>{il.phoneNormalized}</span>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </>
          )}
        </section>

        {!isWizard && (
        <aside className="csm-selected-panel">
          <header>
            <h3>Selecionados</h3>
            <span>{totalSelected}</span>
          </header>

          {totalSelected > 0 && (
            <div className="csm-summary-line">
              {selectedLostCardIds.length > 0 && (
                <span className="csm-summary-badge perdidos">
                  {selectedLostCardIds.length} na seleção (aguardando contato)
                </span>
              )}
              {selectedInativosLostCardIds.length > 0 && (
                <span className="csm-summary-badge inativos">
                  {selectedInativosLostCardIds.length} inativo(s)
                </span>
              )}
              {selectedPartnerIds.length > 0 && (
                <span className="csm-summary-badge parceiros">{selectedPartnerIds.length} parceiro(s)</span>
              )}
              {selectedCsvLeadPhones.length > 0 && (
                <span className="csm-summary-badge csv">{selectedCsvLeadPhones.length} csv</span>
              )}
            </div>
          )}

          <div className="csm-list">
            {totalSelected === 0 ? (
              <p className="csm-empty">Nenhum item selecionado.</p>
            ) : (
              <>
                {selectedLostCardIds.map((id) => {
                  const card = lostCards.find((c) => c.lostCardId === id);
                  if (!card) return null;
                  return (
                    <article key={id} className="csm-selected-card">
                      <strong>{card.nome}</strong>
                      <small>Perdido - {isInterested(card) ? 'Interessado' : 'Não interessado'}</small>
                    </article>
                  );
                })}
                {selectedInativosLostCardIds.map((id) => {
                  const card = inativosCards.find((c) => c.lostCardId === id);
                  if (!card) return null;
                  return (
                    <article key={`inativo-${id}`} className="csm-selected-card">
                      <strong>{card.nome}</strong>
                      <small>Inativo — perda {formatDate(card.dataPerda)}</small>
                    </article>
                  );
                })}
                {selectedPartnerIds.map((id) => {
                  const c = clientes.find((cl) => cl.clienteId === id);
                  if (!c) return null;
                  return (
                    <article key={id} className="csm-selected-card">
                      <strong>{c.dadosAdicionais?.nomeContato || c.nome}</strong>
                      <small>Parceiro - {c.dadosAdicionais?.nomeEmpresa || '-'}</small>
                    </article>
                  );
                })}
                {selectedCsvLeadPhones.map((phoneNormalized) => {
                  const il = csvLeads.find((x) => x.phoneNormalized === phoneNormalized);
                  if (!il) return null;
                  return (
                    <article key={phoneNormalized} className="csm-selected-card">
                      <strong>{il.nome}</strong>
                      <small>CSV transitório - {il.telefone}</small>
                    </article>
                  );
                })}
              </>
            )}
          </div>

          <div className="csm-campaign-section">
            <h4>{lockedCampaignId ? 'Adicionar à campanha' : 'Adicionar Campanha'}</h4>
            {lockedCampaignId ? (
              <p className="csm-locked-campaign-hint">
                {campaigns.find((c) => c.campaignId === lockedCampaignId)?.nome || 'Campanha atual'}
              </p>
            ) : (
              <select
                value={selectedCampaignId}
                onChange={(e) => setSelectedCampaignId(e.target.value)}
              >
                <option value="">Selecione uma campanha</option>
                {activeCampaigns.map((c) => (
                  <option key={c.campaignId} value={c.campaignId}>
                    {c.nome}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="csm-primary-button"
              onClick={handleAddToCampaign}
              disabled={totalSelected === 0 || !selectedCampaignId || saving}
            >
              {saving ? 'Salvando...' : 'Adicionar à campanha'}
            </button>
          </div>
        </aside>
        )}
      </div>
    </section>
  );
});
